/**
 * Self-hosted error logger — replaces external Sentry.
 *
 * Captures:
 *   - Uncaught errors (window.onerror)
 *   - Unhandled promise rejections (window.onunhandledrejection)
 *   - React render errors (via captureException from ErrorBoundary)
 *   - Manual: captureException(error, context), captureMessage(msg, level)
 *
 * Behaviour:
 *   - Fingerprints by message+first-stack-line so the same error groups in DB
 *   - Dedup window 30s in-memory: same fingerprint within 30s → drop, only count
 *   - Batch flush: every 5s OR when 5 events queued
 *   - Hard cap: max 50 RPC calls per session (prevents infinite loops from
 *     spamming our own DB if the logger itself misbehaves)
 *   - Disabled when VITE_E2E_MOCKS=true to keep tests clean
 *   - Best-effort: never throws — failure to log must not break user UX
 */

import { kanbify } from './kanbifyClient'

const e2eMocks = import.meta.env.VITE_E2E_MOCKS === 'true'
const PROJECT = 'kanbify'
const FLUSH_INTERVAL_MS = 5000
const FLUSH_THRESHOLD = 5
const DEDUP_WINDOW_MS = 30_000
const MAX_RPC_CALLS_PER_SESSION = 50

let queue = []
let recentFingerprints = new Map() // fingerprint → last sent timestamp
let rpcCallCount = 0
let flushTimer = null
let initialized = false

export const isErrorLoggerEnabled = !e2eMocks

function fingerprint(message, stack) {
    const firstStackLine = (stack || '').split('\n')[1]?.trim().slice(0, 200) || ''
    const raw = `${message?.slice(0, 200) || ''}|${firstStackLine}`
    // Lightweight hash (FNV-1a 32-bit) — sufficient for grouping
    let h = 0x811c9dc5
    for (let i = 0; i < raw.length; i++) {
        h ^= raw.charCodeAt(i)
        h = Math.imul(h, 0x01000193) >>> 0
    }
    return h.toString(16).padStart(8, '0')
}

function shouldDrop(fp) {
    const now = Date.now()
    // GC stale entries
    if (recentFingerprints.size > 100) {
        for (const [k, t] of recentFingerprints) {
            if (now - t > DEDUP_WINDOW_MS) recentFingerprints.delete(k)
        }
    }
    const last = recentFingerprints.get(fp)
    if (last && now - last < DEDUP_WINDOW_MS) return true
    recentFingerprints.set(fp, now)
    return false
}

function scheduleFlush() {
    if (flushTimer) return
    flushTimer = setTimeout(() => {
        flushTimer = null
        flush()
    }, FLUSH_INTERVAL_MS)
}

async function flush() {
    if (!queue.length) return
    if (rpcCallCount >= MAX_RPC_CALLS_PER_SESSION) {
        queue = [] // drop on the floor; we've hit the budget
        return
    }
    const batch = queue
    queue = []
    rpcCallCount += batch.length

    // Send sequentially to avoid hammering DB if many errors arrive at once
    for (const evt of batch) {
        try {
            await kanbify.rpc('log_error', {
                p_message: evt.message,
                p_stack: evt.stack,
                p_url: evt.url,
                p_user_agent: evt.userAgent,
                p_fingerprint: evt.fingerprint,
                p_level: evt.level,
                p_context: evt.context,
                p_project: PROJECT,
            })
        } catch {
            // Silent fail — never let logging itself raise
        }
    }
}

function enqueue(event) {
    if (!isErrorLoggerEnabled) return
    if (shouldDrop(event.fingerprint)) return
    queue.push(event)
    if (queue.length >= FLUSH_THRESHOLD) {
        flush()
    } else {
        scheduleFlush()
    }
}

function buildEvent(level, message, stack, context = {}) {
    const fp = fingerprint(message, stack)
    return {
        level,
        message: String(message || '<empty>').slice(0, 2000),
        stack: stack ? String(stack).slice(0, 8000) : null,
        url: typeof window !== 'undefined' ? window.location.href.slice(0, 1000) : '',
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 500) : '',
        fingerprint: fp,
        context: context || {},
    }
}

export function captureException(error, context = {}) {
    if (!isErrorLoggerEnabled || !error) return
    try {
        const message = error?.message ?? String(error)
        const stack = error?.stack ?? null
        enqueue(buildEvent('error', message, stack, context))
    } catch {
        // never throw
    }
}

export function captureMessage(message, level = 'info', context = {}) {
    if (!isErrorLoggerEnabled || !message) return
    try {
        enqueue(buildEvent(level, message, null, context))
    } catch {
        // never throw
    }
}

export function captureReactError(error, errorInfo) {
    captureException(error, {
        componentStack: errorInfo?.componentStack || null,
        source: 'react_error_boundary',
    })
}

/**
 * Initialize global error/rejection handlers. Call once at app start.
 * Idempotent.
 */
export function initializeErrorLogger() {
    if (!isErrorLoggerEnabled || initialized || typeof window === 'undefined') return
    initialized = true

    window.addEventListener('error', (event) => {
        if (!event) return
        captureException(event.error || new Error(event.message || 'Unknown error'), {
            source: 'window.onerror',
            filename: event.filename,
            lineno: event.lineno,
            colno: event.colno,
        })
    })

    window.addEventListener('unhandledrejection', (event) => {
        const reason = event?.reason
        const error = reason instanceof Error ? reason : new Error(String(reason || 'Unhandled rejection'))
        captureException(error, { source: 'unhandledrejection' })
    })

    // Flush queued events before page unload
    window.addEventListener('beforeunload', () => {
        if (queue.length > 0) flush()
    })
}

// Useful for tests
export function _resetErrorLoggerForTests() {
    queue = []
    recentFingerprints = new Map()
    rpcCallCount = 0
    if (flushTimer) {
        clearTimeout(flushTimer)
        flushTimer = null
    }
    initialized = false
}
